const { isRecoverableServerError } = require('utils/network');

const internals = {};

exports.createServiceUnavailableRecovery = ({
    http,
    onAvailable = () => {},
    onUnavailable = () => {},
    wait = internals.wait
}) => {

    let manualRetryInProgress = false;
    let pendingRequests = [];

    const retryRequest = (config) => http.request({
        ...config,
        serviceUnavailableAttempt: (config.serviceUnavailableAttempt || 0) + 1
    });

    const handleResponseError = async (error) => {

        const { config = {} } = error || {};

        if (!config.serviceUnavailableRetry || !isRecoverableServerError(error)) {
            return Promise.reject(error);
        }

        if (config.serviceUnavailableAttempt) {
            return Promise.reject(error);
        }

        await wait(internals.retryDelay());

        try {
            return await retryRequest(config);
        }
        catch (retryError) {
            if (!isRecoverableServerError(retryError)) {
                throw retryError;
            }

            return new Promise((resolve, reject) => {

                pendingRequests.push({
                    config,
                    error: retryError,
                    reject,
                    resolve
                });

                if (pendingRequests.length === 1) {
                    onUnavailable();
                }
            });
        }
    };

    const handleTransientError = async (error) => {

        const { config = {} } = error || {};

        if (!config.serviceUnavailableRetry || config.serviceUnavailableAttempt) {
            return Promise.reject(error);
        }

        await wait(internals.retryDelay());

        return retryRequest(config);
    };

    const retryPendingRequest = async () => {

        if (!pendingRequests.length || manualRetryInProgress) {
            return undefined;
        }

        const requestsBeingRetried = [...pendingRequests];

        manualRetryInProgress = true;
        let changedPendingRequests = false;

        try {
            const results = await Promise.all(requestsBeingRetried.map(async (requestBeingRetried) => {

                try {
                    const response = await retryRequest(requestBeingRetried.config);

                    if (pendingRequests.includes(requestBeingRetried)) {
                        pendingRequests = pendingRequests.filter((request) => request !== requestBeingRetried);
                        changedPendingRequests = true;
                        requestBeingRetried.resolve(response);
                    }

                    return response;
                }
                catch (error) {
                    if (!pendingRequests.includes(requestBeingRetried)) {
                        return undefined;
                    }

                    if (isRecoverableServerError(error)) {
                        requestBeingRetried.error = error;
                        return undefined;
                    }

                    pendingRequests = pendingRequests.filter((request) => request !== requestBeingRetried);
                    changedPendingRequests = true;
                    requestBeingRetried.reject(error);

                    return undefined;
                }
            }));

            if (changedPendingRequests && !pendingRequests.length) {
                onAvailable();
            }

            return results.length === 1 ? results[0] : results;
        }
        finally {
            manualRetryInProgress = false;
        }
    };

    const continueWithoutRetry = () => {

        if (!pendingRequests.length) {
            return;
        }

        const abandonedRequests = pendingRequests;

        pendingRequests = [];
        onAvailable();
        abandonedRequests.forEach((request) => request.reject(request.error));
    };

    return {
        continueWithoutRetry,
        handleResponseError,
        handleTransientError,
        hasPendingRequest: () => !!pendingRequests.length,
        retryPendingRequest
    };
};

internals.retryDelay = () => 500 + Math.floor(Math.random() * 501);

internals.wait = (delay) => new Promise((resolve) => setTimeout(resolve, delay));
